/**
 * @typedef {object} getSelectedClassificationReturnObject
 * @property {boolean} [hasSelectedClassification] - if a classification is selected on a registration
 * @property {object} [selectedClassification] - if a classification is selected on a registration,
 *                    the redux classification object. If a classification is not selected, an empty object
 */

/**
 * @public
 * @function selectUserSelectedClassificationSelector
 * @description This selector takes in redux state and derives what classification has been selected
 *              during registration. It will return a hasSelectedClassification boolean and the
 *              selectedClassification
 * @param {object} [registration] redux registration state object
 * @param {Array} [classifications] redux classifications state array of objects
 * @returns {object} [getSelectedClassificationReturnObject] - hasSelectedClassification boolean
 *                   and selectedClassification object
 */
export const selectUserSelectedClassificationSelector = (
    registration,
    classifications,
) => {
    const { classification = {} } = registration;

    const isSelected = Object.keys(classification).length > 0;
    let selectedClassification = {};
    let hasSelectedClassification = false;
    // early return if no selected classification
    if (!isSelected) {
        return { hasSelectedClassification, selectedClassification };
    }

    const selectedClassificationId =
        classification &&
        classification.Classification &&
        classification.Classification.length > 0
            ? classification.Classification[0]
            : "";

    // early return is no classification id match (junk data)
    if (!selectedClassificationId) {
        return { hasSelectedClassification, selectedClassificationId };
    }

    const classificationIds = classifications.map(
        (_classification) => _classification.id,
    );
    const idExistsInClassifications = classificationIds.includes(
        Number(selectedClassificationId),
    );

    // early return is no classification id match in classifications (junk data)
    if (!idExistsInClassifications) {
        return { hasSelectedClassification, selectedClassification };
    }

    selectedClassification =
        classifications.find(
            (_classification) =>
                _classification.id === Number(selectedClassificationId),
        ) || {};

    hasSelectedClassification = Object.keys(selectedClassification).length > 0;

    return {
        hasSelectedClassification,
        selectedClassification,
    };
};

/**
 *
 * @param {object} classifications Classifications object from Redux Store.
 * @param {object} p2pSettings P2P Settings object from Redux Store.
 * @description Determine if there are classifications based on settings
 * @returns {boolean} hasClassifications
 */
export const selectHasClassificationsSelector = (
    classifications,
    p2pSettings,
) => {
    const { enableClassifications } = p2pSettings;
    const hasClassifications =
        enableClassifications && classifications.length > 0;

    return hasClassifications;
};
